import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import NavigationBar from '../components/NavigationBar';
import RealVideoChat from '../components/RealVideoChat';
import DecisionModal from '../components/modals/DecisionModal';
import FilterModal from '../components/modals/FilterModal';
import ComplaintModal from '../components/modals/ComplaintModal';
import NoMatchModal from '../components/modals/NoMatchModal';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import api from '../lib/api';
import { initSocket, disconnectSocket } from '../lib/socket';

const VideoChat = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [matchUser, setMatchUser] = useState(null);
  const [session, setSession] = useState(null);
  const [timeLeft, setTimeLeft] = useState(600); // 10 minutes
  const [showDecision, setShowDecision] = useState(false);
  const [decisionWaiting, setDecisionWaiting] = useState(false);
  const [decisionTimerStart, setDecisionTimerStart] = useState(null); // Синхронизированное время начала
  const [showFilters, setShowFilters] = useState(false);
  const [showComplaint, setShowComplaint] = useState(false);
  const [showNoMatch, setShowNoMatch] = useState(false);
  const [searching, setSearching] = useState(false);
  const [searchStatus, setSearchStatus] = useState('');
  const [chatActive, setChatActive] = useState(false); // Отслеживаем активный чат
  const timerRef = useRef(null);
  const searchTimeoutRef = useRef(null);
  const decisionMadeRef = useRef(false); // Отслеживаем, было ли принято решение
  
  // Initialize socket once using useMemo
  const socket = useMemo(() => initSocket(), []);

  // Redirect if profile not completed
  useEffect(() => {
    if (!user?.profile_completed) {
      navigate('/complete-profile');
    }
  }, [user, navigate]);

  // Format time helper
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Reset chat state - полный сброс всех состояний
  const resetChat = useCallback(() => {
    setMatchUser(null);
    setSession(null);
    setTimeLeft(600);
    setChatActive(false);
    setShowDecision(false);
    setDecisionWaiting(false);
    decisionMadeRef.current = false;
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  // Cancel search
  const cancelSearch = useCallback(() => {
    if (socket && user) {
      socket.emit('cancel_search', { user_id: user.id });
    }
    setSearching(false);
    setSearchStatus('');
    
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
  }, [user, socket]);

  // Handle time end
  const handleTimeEnd = useCallback(() => {
    // Останавливаем таймер
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    
    // Предотвращаем повторные вызовы
    if (!chatActive || decisionMadeRef.current) return;
    
    // СРАЗУ завершаем видеочат на UI
    setChatActive(false);
    
    // Отправляем через WebSocket для синхронизации с партнёром
    if (session && socket && user) {
      socket.emit('end_video_chat', {
        session_id: session.id,
        user_id: user.id
      });
    }
    
    // Показываем модалку решения
    setShowDecision(true);
  }, [session, socket, user, chatActive]);

  // Handle end chat (when user clicks hang up button)
  const handleEndChat = useCallback(() => {
    if (!session || !socket || !user) return;
    
    // Останавливаем таймер
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    
    // СРАЗУ завершаем видеочат на UI (не ждём ответа сервера)
    setChatActive(false);
    
    // Отправляем через WebSocket для синхронизации с партнёром
    socket.emit('end_video_chat', {
      session_id: session.id,
      user_id: user.id
    });
    
    // Показываем модалку решения
    setShowDecision(true);
  }, [session, socket, user]);

  // Initialize socket connection for search
  useEffect(() => {
    // Слушаем события поиска
    socket.on('match_found', (data) => {
      console.log('Match found!', data);
      setSearching(false);
      setSearchStatus('');
      setMatchUser(data.partner);
      setSession(data.session);
      setTimeLeft(600);
      setChatActive(true); // Чат начался
      decisionMadeRef.current = false;
      toast.success(`Собеседник найден: ${data.partner.name}!`);
      
      // Очищаем таймаут поиска
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    });
    
    socket.on('searching', (data) => {
      console.log('Searching...', data);
      setSearchStatus(data.message);
    });
    
    socket.on('search_error', (data) => {
      console.error('Search error:', data);
      setSearching(false);
      setSearchStatus('');
      
      if (data.error === 'no_communications') {
        toast.error('У вас закончились бесплатные общения на сегодня');
        navigate('/subscriptions');
      } else {
        toast.error(data.message || data.error || 'Ошибка поиска');
      }
    });
    
    socket.on('search_cancelled', () => {
      setSearching(false);
      setSearchStatus('');
    });
    
    // Нет пользователей с такими критериями
    socket.on('no_users_available', () => {
      setSearching(false);
      setSearchStatus('');
      // Очищаем таймаут поиска
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
      // Показываем модалку "нет совпадений"
      setShowNoMatch(true);
    });
    
    socket.on('peer_disconnected', () => {
      // Только если чат активен и решение ещё не принято
      if (!chatActive || decisionMadeRef.current) return;
      
      toast.info('Собеседник отключился');
      // Останавливаем таймер и показываем модалку решения
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      setChatActive(false);
      setShowDecision(true);
    });
    
    // Синхронизация завершения чата
    socket.on('chat_ended', (data) => {
      console.log('Chat ended:', data);
      // Только если решение ещё не принято
      if (decisionMadeRef.current) return;
      
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      
      // Завершаем видеочат на UI
      setChatActive(false);
      
      // Сохраняем синхронизированное время начала таймера
      if (data.decision_timer_start) {
        setDecisionTimerStart(data.decision_timer_start);
      }
      
      // Показываем модалку сразу (без задержки!)
      if (data.show_decision) {
        setShowDecision(true);
        setDecisionWaiting(false);
      }
    });
    
    // Ожидание решения партнёра
    socket.on('decision_waiting', () => {
      setDecisionWaiting(true);
    });
    
    // Результат решения
    socket.on('decision_result', (data) => {
      console.log('Decision result:', data);
      
      // Всегда обрабатываем decision_result - это финальное решение сервера
      decisionMadeRef.current = true; // Отмечаем что решение принято
      setShowDecision(false);
      setDecisionWaiting(false);
      
      if (data.matched) {
        if (data.existing) {
          toast.success('У вас уже есть чат с этим пользователем!');
        } else {
          toast.success('Взаимная симпатия! Чат открыт');
        }
        resetChat();
        navigate(`/chat/${data.match_id}`);
      } else if (data.partner_declined) {
        // Партнёр нажал "нет" - просто уведомляем и сбрасываем
        toast.info('Собеседник не заинтересован в продолжении общения');
        resetChat();
      } else if (data.show_complaint) {
        // Текущий пользователь нажал "нет" - предлагаем оставить жалобу
        setShowComplaint(true);
      } else {
        toast.info('Собеседник не заинтересован');
        resetChat();
      }
    });
    
    socket.on('decision_error', (data) => {
      toast.error(data.error || 'Ошибка обработки решения');
      decisionMadeRef.current = true;
      setShowDecision(false);
      resetChat();
    });
    
    return () => {
      socket.off('match_found');
      socket.off('searching');
      socket.off('search_error');
      socket.off('search_cancelled');
      socket.off('no_users_available');
      socket.off('peer_disconnected');
      socket.off('chat_ended');
      socket.off('decision_waiting');
      socket.off('decision_result');
      socket.off('decision_error');
      // НЕ отключаем сокет здесь - он нужен для видеочата!
    };
  }, [navigate, resetChat, socket, chatActive]);
  
  // Отключаем сокет только при размонтировании компонента
  useEffect(() => {
    return () => {
      disconnectSocket();
    };
  }, []);

  // Timer for video chat - 10 минут обратного отсчёта
  useEffect(() => {
    // Очищаем предыдущий таймер при изменении состояния
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    
    // Запускаем таймер только если есть активная сессия и чат активен
    if (session && chatActive && timeLeft > 0) {
      timerRef.current = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 1) {
            // Используем setTimeout чтобы не вызывать setState во время рендера
            setTimeout(() => handleTimeEnd(), 0);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [session, chatActive, handleTimeEnd]);

  // Find match via WebSocket
  const findMatch = useCallback(() => {
    if (!socket || !user) return;
    
    setSearching(true);
    setSearchStatus('Подключаемся к серверу поиска...');
    
    // Отправляем запрос на поиск через WebSocket
    socket.emit('start_search', {
      user_id: user.id
    });
    
    // Таймаут поиска - 2 минуты
    searchTimeoutRef.current = setTimeout(() => {
      cancelSearch();
      setShowNoMatch(true);
    }, 120000);
  }, [user, cancelSearch, socket]);

  // Handle decision after video chat - через WebSocket для синхронизации
  const handleDecision = useCallback((accepted) => {
    if (!session || !socket || !user) return;
    
    // Отправляем решение через WebSocket
    socket.emit('submit_decision', {
      session_id: session.id,
      user_id: user.id,
      decision: accepted
    });
    
    // Показываем ожидание пока не получим результат
    setDecisionWaiting(true);
  }, [session, socket, user]);

  // Обработка таймаута режима ожидания - если партнёр не ответил за минуту
  const handleWaitingTimeout = useCallback(() => {
    console.log('Waiting timeout - treating as partner declined');
    setShowDecision(false);
    setDecisionWaiting(false);
    setSession(null);
    setMatchUser(null);
    toast.info('Собеседник не ответил за отведённое время');
  }, []);

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Animated background */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#F8F9FA] via-white to-[#F0F4FF]" />
      <div className="absolute top-20 right-10 w-96 h-96 bg-[#FF5757]/10 rounded-full blur-3xl animate-pulse" />
      <div className="absolute bottom-20 left-10 w-80 h-80 bg-[#34C759]/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1.5s' }} />
      
      <div className="relative z-10 flex flex-col min-h-screen">
        <NavigationBar />
      
      <div className="flex-1 flex flex-col items-center justify-start pt-8 p-4" data-testid="videochat-container">
        {!session ? (
          <div className="w-full max-w-4xl space-y-6">
            <div className="bg-gradient-to-br from-[#2C2C54] to-[#474787] rounded-3xl p-8 aspect-video flex items-center justify-center shadow-2xl border border-white/10">
              <div className="text-center">
                {searching ? (
                  <>
                    <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-white/10 flex items-center justify-center">
                      <div className="w-16 h-16 border-4 border-white/30 border-t-white rounded-full animate-spin"></div>
                    </div>
                    <p className="text-white/90 text-lg font-medium">
                      {searchStatus || 'Ищем подходящего собеседника...'}
                    </p>
                    <p className="text-white/50 text-sm mt-2">
                      Ожидаем пользователя с подходящими критериями
                    </p>
                    <Button
                      onClick={cancelSearch}
                      variant="outline"
                      className="mt-6 px-8 py-2 rounded-full border-white/30 text-white hover:bg-white/10"
                      data-testid="cancel-search-button"
                    >
                      Отменить поиск
                    </Button>
                  </>
                ) : (
                  <>
                    <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-white/10 flex items-center justify-center">
                      <svg className="w-12 h-12 text-white/80" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <p className="text-white/80 text-lg">
                      Нажмите &quot;Знакомиться&quot; чтобы начать видео-знакомство
                    </p>
                    <p className="text-white/50 text-sm mt-2">
                      Убедитесь, что камера и микрофон разрешены
                    </p>
                  </>
                )}
              </div>
            </div>

            <div className="flex flex-col items-center gap-4">
              <button
                onClick={() => setShowFilters(true)}
                className="text-[#1A73E8] font-medium hover:underline"
                data-testid="filters-button"
              >
                Настроить фильтры поиска
              </button>

              {!searching && (
                <Button
                  onClick={findMatch}
                  disabled={searching}
                  className="w-full max-w-md py-6 rounded-full text-white font-semibold text-lg shadow-lg hover:shadow-xl transition-all"
                  style={{ background: 'linear-gradient(135deg, #34C759 0%, #5DD97C 100%)' }}
                  data-testid="find-match-button"
                >
                  ЗНАКОМИТЬСЯ
                </Button>
              )}
            </div>
          </div>
        ) : (
          <div className="w-full max-w-4xl">
            {chatActive ? (
              <RealVideoChat
                matchUser={matchUser}
                sessionId={session.id}
                userId={user?.id}
                socket={socket}
                onTimeEnd={handleTimeEnd}
                onEndChat={handleEndChat}
                timeLeft={timeLeft}
                formatTime={formatTime}
              />
            ) : (
              <div className="bg-gradient-to-br from-[#2C2C54] to-[#474787] rounded-3xl p-8 aspect-video flex items-center justify-center shadow-2xl border border-white/10">
                <div className="text-center text-white">
                  <p className="text-lg">Видеозвонок завершён</p>
                  <p className="text-white/60 text-sm mt-2">Ожидаем вашего решения...</p>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <DecisionModal
        isOpen={showDecision}
        onDecision={handleDecision}
        waiting={decisionWaiting}
        onWaitingTimeout={handleWaitingTimeout}
        syncStartTime={decisionTimerStart}
      />

      <FilterModal
        isOpen={showFilters}
        onClose={() => setShowFilters(false)}
      />

      <NoMatchModal
        isOpen={showNoMatch}
        onClose={() => setShowNoMatch(false)}
        onOpenFilters={() => setShowFilters(true)}
      />

      <ComplaintModal
        isOpen={showComplaint}
        onClose={() => {
          setShowComplaint(false);
          resetChat();
        }}
        reportedUserId={matchUser?.id}
      />
      </div>
    </div>
  );
};

export default VideoChat;
